import React, { useEffect, useRef, useState } from 'react';

// --- Shared Reveal Logic ---
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0) translateX(0)';
            }, i * 120);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: 'up' | 'left' | 'right' = 'up'): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === 'up' ? 'translateY(28px)'
    : dir === 'left' ? 'translateX(-32px)'
    : 'translateX(32px)',
  transition: 'opacity 0.8s ease, transform 0.8s ease',
});
// ---------------------------

const RECIPIENT = 'info@bicci-it.com';

export default function ContactSection() {
  const sectionRef = useReveal();
  const [status, setStatus] = useState<'idle' | 'success'>('idle');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);

    const firstName  = data.get('firstName')  as string;
    const lastName   = data.get('lastName')   as string;
    const email      = data.get('email')      as string;
    const company    = data.get('company')    as string;
    const trade      = data.get('trade')      as string;
    const message    = data.get('message')    as string;

    const subject = encodeURIComponent(
      `BICCI Contact – ${firstName} ${lastName} (${company})`
    );

    const body = encodeURIComponent(
      `Name: ${firstName} ${lastName}\n` +
      `Email: ${email}\n` +
      `Company: ${company}\n` +
      `Trade License Number: ${trade}\n\n` +
      `Message:\n${message}`
    );

    window.location.href = `mailto:${RECIPIENT}?subject=${subject}&body=${body}`;

    setStatus('success');
    form.reset();
    setTimeout(() => setStatus('idle'), 4000);
  };

  return (
    <section id="contact" className="bg-white py-20 md:py-24 font-sans relative overflow-hidden">

      {/* Subtle Background Accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#F8F7F3] to-transparent opacity-50 pointer-events-none" />

      <div ref={sectionRef} className="relative z-10 w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
          <h2
            data-r
            style={{
              ...hide('up'),
              color: '#0F1C35',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em'
            }}
            className="mb-6"
          >
            Contact Us
          </h2>
          <p
            data-r
            style={{ ...hide('up'), color: '#3D4A62' }}
            className="text-base md:text-lg leading-relaxed"
          >
            For membership inquiries, partnership opportunities, institutional cooperation, or general information, please contact the Bangladesh Italy Chamber of Commerce and Industry (BICCI).
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div data-r style={hide('up')}>
            <form
              id="bicci-contact"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10 border shadow-lg shadow-black/[0.03]"
              style={{ borderColor: 'rgba(15, 28, 53, 0.08)' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="fname" className="block text-sm font-bold text-[#0F1C35] mb-2">First Name</label>
                  <input
                    id="fname"
                    name="firstName"
                    type="text"
                    placeholder="Your first name"
                    required
                    className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="lname" className="block text-sm font-bold text-[#0F1C35] mb-2">Last Name</label>
                  <input
                    id="lname"
                    name="lastName"
                    type="text"
                    placeholder="Your last name"
                    required
                    className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="email" className="block text-sm font-bold text-[#0F1C35] mb-2">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@company.com"
                  required
                  className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="company" className="block text-sm font-bold text-[#0F1C35] mb-2">Company</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  placeholder="Your company name"
                  className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="trade" className="block text-sm font-bold text-[#0F1C35] mb-2">Trade License Number</label>
                <input
                  id="trade"
                  name="trade"
                  type="number"
                  required
                  placeholder="Your trade license number"
                  className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all"
                />
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-bold text-[#0F1C35] mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  placeholder="Tell us how we can help you..."
                  required
                  maxLength={500}
                  className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-[#0F1C35] placeholder:text-gray-400 focus:outline-none focus:border-[#0077b6] focus:ring-4 focus:ring-[#0077b6]/10 focus:bg-white transition-all resize-none"
                />
                <p className="text-xs text-gray-400 mt-2 text-right">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="group w-full px-6 py-4 text-white text-base font-bold uppercase tracking-wide rounded-xl transition-all duration-300 cursor-pointer flex items-center justify-center gap-3 relative overflow-hidden shadow-lg"
                style={{ backgroundColor: '#0077b6' }}
              >
                <div className="absolute inset-0 w-full h-full bg-[#005f8e] transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-0" />
                <span className="relative z-10 flex items-center gap-3">
                  <span>Send Message</span>
                  <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                </span>
              </button>

              {status === 'success' && (
                <div className="mt-5 flex items-center gap-3 px-5 py-4 rounded-xl bg-green-50 border border-green-200 text-sm font-medium text-green-800">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Your email client has opened with the message pre-filled. Please hit send to complete your submission.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}